import {
  useCallback, useMemo, useState, useEffect,
} from 'react'
import { PGA_COACH_SAVED_OFFERINGS_KEY } from '@lib/pga-coach/constants'
import { getSavedOfferingIds, toggleSavedOffering } from '@lib/pga-coach/saved-offerings'

/**
 * Tracks offering IDs persisted in localStorage for the coach journey listing.
 * Incrementing revision after toggles keeps filters and hearts in sync in the same tab.
 */
export default function usePgaCoachSavedOfferings() {
  const [savedOfferingIds, setSavedOfferingIds] = useState([])

  const savedIdSet = useMemo(
    () => new Set(savedOfferingIds),
    [savedOfferingIds],
  )

  const refresh = useCallback(() => {
    setSavedOfferingIds(getSavedOfferingIds())
  }, [])

  const toggle = useCallback((offeringId) => {
    const nextSaved = toggleSavedOffering(offeringId)
    refresh()
    return nextSaved
  }, [refresh])

  useEffect(() => {
    refresh()
    const handleStorage = (event) => {
      if (event.key === PGA_COACH_SAVED_OFFERINGS_KEY) refresh()
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [refresh])

  const isOfferingSaved = useCallback(
    (offeringId) => (offeringId != null ? savedIdSet.has(String(offeringId)) : false),
    [savedIdSet],
  )

  return {
    savedIdSet, toggle, refresh, isOfferingSaved,
  }
}
